import { z } from "zod";
/**
 * Specialties ReassureMed actually coordinates for. A closed list rather than
 * free text so that browsing, the by-specialty index and the seed data cannot
 * drift apart on spelling.
 */
export const SPECIALTIES = [
    "oncology",
    "cardiology",
    "cardiac_surgery",
    "orthopaedics",
    "spine_surgery",
    "neurology",
    "neurosurgery",
    "gastroenterology",
    "nephrology",
    "transplant",
    "fertility",
];
export const specialtySchema = z.enum(SPECIALTIES);
export function specialtyLabel(value) {
    switch (value) {
        case "cardiac_surgery":
            return "Cardiac surgery";
        case "spine_surgery":
            return "Spine surgery";
        case "orthopaedics":
            return "Orthopaedics";
        default:
            return value.charAt(0).toUpperCase() + value.slice(1);
    }
}
/** Sort key discriminators in the clinicians table. */
export const PROFILE_SK = "PROFILE";
export const SLOT_SK_PREFIX = "SLOT#";
export const slotSortKey = (startsAt) => `${SLOT_SK_PREFIX}${startsAt}`;
/**
 * A bookable window.
 *
 * `held` exists so a slot can be taken out of circulation without an
 * appointment yet existing - a coordinator arranging a time by phone, for
 * instance. Nothing in the demo path produces it, but leaving it out would
 * mean modelling that later as a boolean bolted onto `available`.
 */
export const SLOT_STATES = ["available", "held", "booked"];
export const availabilitySlotInputSchema = z.object({
    starts_at: z.string().datetime(),
    duration_minutes: z.number().int().min(15).max(180).default(45),
});
export const setAvailabilitySchema = z.object({
    /**
     * The complete set of open windows being offered. Replacing rather than
     * appending is what lets a specialist withdraw a slot, and slots that are
     * already booked are never touched by this - the handler refuses to remove
     * a window somebody is relying on.
     */
    slots: z.array(availabilitySlotInputSchema).max(200),
});
export function toSummary(profile) {
    return {
        clinician_id: profile.clinician_id,
        name: profile.name,
        specialty: profile.specialty,
        credentials: profile.credentials,
        bio: profile.bio,
        timezone: profile.timezone,
        languages: profile.languages,
        hospital: profile.hospital,
        country: profile.country,
        years_experience: profile.years_experience,
    };
}
/**
 * Whether a window can still be booked.
 *
 * The rule is that the consultation must not already be *over* - not that it
 * must be in the future. Booking a window that has just started is unusual but
 * coherent: an urgent same-hour consultation is exactly the thing a
 * coordination team arranges. Booking one that finished last Tuesday is not
 * coherent under any reading, and until this existed nothing stopped it.
 */
export function isSlotBookable(slot, now = new Date()) {
    if (slot.state !== "available")
        return false;
    const endsAt = Date.parse(slot.ends_at);
    if (Number.isNaN(endsAt))
        return false;
    return endsAt > now.getTime();
}
/** True once a window is over, whether or not anyone booked it. */
export function isSlotPast(slot, now = new Date()) {
    const endsAt = Date.parse(slot.ends_at);
    return !Number.isNaN(endsAt) && endsAt <= now.getTime();
}
// Media region selection lives in media.ts - it follows the patient, not the
// specialist, so it does not belong on the clinician model.
//# sourceMappingURL=clinician.js.map